/* ============================================================
   Resolves the synced appearance preference (light / dark / system) against
   the OS and writes it to <html data-theme>, which tokens.css keys on. The
   preference comes from PreferencesProvider, so this sits below it.

   `useScheme()` reads the resolved scheme back for the few components that
   draw differently rather than merely recolour (the sky picks white ink on a
   night gradient; on dark every hour is a night gradient).
   ============================================================ */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePreferences } from '@habishine/client-core';

export type Scheme = 'light' | 'dark';

const Ctx = createContext<Scheme>('light');

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { themePref } = usePreferences();
  const [scheme, setScheme] = useState<Scheme>('light');
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const s: Scheme = themePref === 'system' ? (mq.matches ? 'dark' : 'light') : themePref;
      document.documentElement.dataset.theme = s;
      setScheme(s);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [themePref]);
  return <Ctx.Provider value={scheme}>{children}</Ctx.Provider>;
}

export function useScheme(): Scheme {
  return useContext(Ctx);
}

/* The scheme as <html data-theme> has it — readable from anywhere, including
   outside the preference tree (the sign-in page), and live: an observer
   follows the attribute. The sky uses this to pick its ink. */
export function useHtmlScheme(): Scheme {
  const read = (): Scheme => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');
  const [scheme, setScheme] = useState<Scheme>(read);
  useEffect(() => {
    const mo = new MutationObserver(() => setScheme(read()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);
  return scheme;
}

/* Signed out there is no preference to read, so the OS decides and writes the
   attribute tokens.css keys on — otherwise the sign-in page is light on a
   dark desktop. */
export function useOsThemeWhileSignedOut(): void {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => { document.documentElement.dataset.theme = mq.matches ? 'dark' : 'light'; };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
}
